// «ضبط صفحه» of a ticket (60.11.579): one screen recording at a time for the whole panel. It goes on while the customer
// moves between the panel's pages to show the problem, until «پایان ضبط», «انصراف», its limits, the browser's own «Stop
// sharing», or the page closing. A recording that ends away from its ticket waits for the ticket's reply box.
// Since 60.11.580 it runs in the capture window (capture-window.ts), so a refresh of the panel does not end it: the
// panel's tabs mirror it, steer it, and take what was recorded into the ticket's reply box (capture-hub.ts).
import {
  elapsedOnClock,
  RecordingClock,
  RecordingSession,
  TicketRecording,
} from '../recording-session'
import {SCREEN_MAX_SECONDS, SCREEN_TYPES, SCREEN_VIDEO_BITS} from '../ticket-media'
import {CAPTURE_WINDOW_WAIT_MS, inCaptureWindow, openCaptureWindow} from './capture-window'

/** «choosing»: the capture window waits for the customer to choose the screen. */
export type ScreenRecordingStatus = 'idle' | 'choosing' | 'recording' | 'paused' | 'ready'

export type ScreenRecordingState = {
  status: ScreenRecordingStatus
  ticketNumber?: string
  /** «choosing»: the microphone is recorded too. */
  microphone?: boolean
  /** «recording», «paused»: its clock, for every window to show its time. */
  clock?: RecordingClock
  /** «ready»: how long it is, in seconds. */
  seconds?: number
  /** «ready», in the window that holds it: what was recorded. */
  recording?: TicketRecording
}

/** What a panel tab may ask of the capture window's recording. */
export type ScreenRecordingCommand = {
  action: 'pause' | 'resume' | 'stop' | 'cancel' | 'drop' | 'take'
  ticketNumber?: string
}

type Receiver = (recording: TicketRecording) => void

const listeners = new Set<(state: ScreenRecordingState) => void>()
let current: ScreenRecordingState = {status: 'idle'}
let session: RecordingSession | null = null
/** The reply boxes on the screen now, by ticket; the newest takes what was recorded for its ticket. */
const replyBoxes = new Map<string, Receiver[]>()
/** The recording the capture window holds, as a panel tab sees it, and how to ask it for things. */
let remote: {
  state: ScreenRecordingState
  command: (command: ScreenRecordingCommand) => void
} | null = null
/** The capture window was asked once already for the recording it holds. */
let asked = false
let waitTimer = 0

const publish = (next: ScreenRecordingState) => {
  current = next
  listeners.forEach((listener) => listener(current))
  handOver()
}

/** What was recorded for a ticket whose reply box is on the screen goes into it. */
const handOver = () => {
  const ticketNumber = current.ticketNumber
  if (current.status !== 'ready' || !ticketNumber) return
  const boxes = replyBoxes.get(ticketNumber)
  if (!boxes?.length) return
  if (current.recording) {
    const recording = current.recording
    publish({status: 'idle'})
    boxes[boxes.length - 1](recording)
  } else if (remote && !asked) {
    asked = true
    remote.command({action: 'take', ticketNumber})
  }
}

export const getScreenRecording = () => current

export const subscribeScreenRecording = (listener: (state: ScreenRecordingState) => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * A ticket's reply box: it shows the recording's controls while it is on the screen (the bar at the foot of the page then
 * stands aside) and takes what is recorded for its ticket. The answer is called when it goes.
 */
export const showScreenRecordingIn = (ticketNumber: string, receive: Receiver) => {
  replyBoxes.set(ticketNumber, [...(replyBoxes.get(ticketNumber) || []), receive])
  asked = false
  publish({...current})
  return () => {
    const left = (replyBoxes.get(ticketNumber) || []).filter((box) => box !== receive)
    if (left.length) replyBoxes.set(ticketNumber, left)
    else replyBoxes.delete(ticketNumber)
    publish({...current})
  }
}

/** Whether the recording's own ticket shows it in its reply box. */
export const screenRecordingInReplyBox = (state: ScreenRecordingState = current) =>
  !!state.ticketNumber && replyBoxes.has(state.ticketNumber)

/** Whether a screen recording is asked for, runs (paused or not) or waits for its ticket. */
export const screenRecordingBusy = (state: ScreenRecordingState = current) =>
  state.status !== 'idle'

/** How long it has run, its pauses left out, in seconds. */
export const screenRecordingSeconds = (state: ScreenRecordingState = current) => {
  if (session) return session.elapsed()
  if (state.clock) return elapsedOnClock(state.clock)
  return state.seconds ?? state.recording?.seconds ?? 0
}

/**
 * A panel tab beside a capture window: the recording the window holds (null: none any more) and how to ask it for
 * things. A recording of this page's own goes first.
 */
export const mirrorScreenRecording = (
  state: ScreenRecordingState | null,
  command: (command: ScreenRecordingCommand) => void = () => undefined
) => {
  const next = state && state.status !== 'idle' ? state : null
  if (!next && !remote) return
  if (next?.status !== 'ready') asked = false
  remote = next ? {state: next, command} : null
  if (session || current.recording) return
  window.clearTimeout(waitTimer)
  publish(next || {status: 'idle'})
}

/** A panel tab: what the capture window recorded for a ticket, asked for by this tab's reply box. */
export const receiveScreenRecording = (ticketNumber: string, file: File, seconds: number) => {
  const recording: TicketRecording = {file, url: URL.createObjectURL(file), seconds, kind: 'screen'}
  const boxes = replyBoxes.get(ticketNumber)
  if (boxes?.length) boxes[boxes.length - 1](recording)
  // The reply box went in the meantime: it waits here instead.
  else if (!session) publish({status: 'ready', ticketNumber, recording, seconds})
}

/**
 * From a panel page: the capture window is asked to record for the ticket, and the customer chooses the screen there.
 * False where the browser opens no window: the page then records itself.
 */
export const requestScreenRecording = (ticketNumber: string, microphone: boolean) => {
  if (inCaptureWindow() || !openCaptureWindow({kind: 'record', ticketNumber, microphone}))
    return false
  if (!remote) publish({status: 'choosing', ticketNumber, microphone})
  // A window that never answers (closed at once, or its page failed) lets the request go.
  window.clearTimeout(waitTimer)
  waitTimer = window.setTimeout(() => {
    if (!remote && !session && current.status === 'choosing') publish({status: 'idle'})
  }, CAPTURE_WINDOW_WAIT_MS)
  return true
}

/** In the capture window: the customer is to choose the screen to record for the ticket. */
export const prepareScreenRecording = (ticketNumber: string, microphone: boolean) => {
  if (session || current.recording) return
  publish({status: 'choosing', ticketNumber, microphone})
}

/**
 * The browser asks the customer for a screen, a window or a tab (and the microphone, when asked for): the streams to
 * record - none when the customer chose nothing - and what went wrong, to say.
 */
export const askScreenForRecording = async (
  microphone: boolean
): Promise<{streams: MediaStream[]; problem?: string}> => {
  const streams: MediaStream[] = []
  try {
    streams.push(
      await navigator.mediaDevices.getDisplayMedia({
        video: {frameRate: {ideal: 12, max: 15}},
        audio: false,
      })
    )
  } catch {
    return {
      streams,
      problem: 'ضبط صفحه شروع نشد؛ برای ضبط، صفحه یا پنجره‌ای را در پنجره‌ی مرورگر انتخاب کنید.',
    }
  }
  if (microphone) {
    try {
      streams.push(await navigator.mediaDevices.getUserMedia({audio: true}))
    } catch {
      return {streams, problem: 'دسترسی به میکروفون داده نشد؛ صفحه بدون صدا ضبط می‌شود.'}
    }
  }
  return {streams}
}

export const RECORDING_UNSUPPORTED =
  'این مرورگر ضبط صفحه را پشتیبانی نمی‌کند؛ از نسخه‌ی تازه‌ی Chrome، Edge یا Firefox استفاده کنید.'

/** The page closing: nothing of it can be kept. */
const onPageHide = () => session?.stop(true)

/**
 * The recording of the screen (and the microphone) the customer chose, for the ticket's reply. A browser that cannot
 * record throws, and the streams are then the caller's to let go.
 */
export const startScreenRecording = (ticketNumber: string, streams: MediaStream[]) => {
  if (session || current.recording || (remote && screenRecordingBusy(remote.state))) return
  const started = new RecordingSession(
    'screen',
    streams,
    SCREEN_TYPES,
    {videoBitsPerSecond: SCREEN_VIDEO_BITS, audioBitsPerSecond: 32_000},
    SCREEN_MAX_SECONDS,
    (recording) => {
      if (session === started) session = null
      window.removeEventListener('pagehide', onPageHide)
      publish(
        recording
          ? {status: 'ready', ticketNumber, recording, seconds: recording.seconds}
          : {status: 'idle'}
      )
    }
  )
  session = started
  window.addEventListener('pagehide', onPageHide)
  publish({status: 'recording', ticketNumber, clock: started.clock()})
}

export const pauseScreenRecording = () => {
  if (!session) remote?.command({action: 'pause'})
  else if (session.pause()) publish({...current, status: 'paused', clock: session.clock()})
}

export const resumeScreenRecording = () => {
  if (!session) remote?.command({action: 'resume'})
  else if (session.resume()) publish({...current, status: 'recording', clock: session.clock()})
}

/** «پایان ضبط» (kept), or «انصراف» with `cancel` - of this page's recording, the capture window's, or a choice. */
export const stopScreenRecording = (cancel = false) => {
  if (session) session.stop(cancel)
  else if (remote) remote.command({action: cancel ? 'cancel' : 'stop'})
  else if (current.status === 'choosing') publish({status: 'idle'})
}

/** The window that holds a finished recording gives it up for its ticket; nothing for another ticket. */
export const takeScreenRecording = (ticketNumber: string) => {
  if (current.status !== 'ready' || current.ticketNumber !== ticketNumber) return null
  const recording = current.recording || null
  publish({status: 'idle'})
  return recording
}

/** «حذف» of a recording that waits for its ticket. */
export const dropScreenRecording = () => {
  if (current.recording) {
    URL.revokeObjectURL(current.recording.url)
    publish({status: 'idle'})
  } else if (remote?.state.status === 'ready') remote.command({action: 'drop'})
}
